/** A numeric column as the report endpoint returns it: a number, a numeric string or null. */
type ReportNumber = number | string | null;

/** One row of `GET /metals/incoming/report` → `grouped`: one metal and purity (per mille). */
export interface IncomingSummaryGroup {
    metal_type: string | null;
    purity: ReportNumber;
    totalQty: ReportNumber;
    totalEuro: ReportNumber;
    totalLei: ReportNumber;
}

export type IncomingSummaryMetal = 'gold' | 'silver' | 'other';

export interface IncomingSummaryRow {
    key: string;
    /** Metal and title, e.g. `AUR 585`. */
    label: string;
    /** The title as a percentage, e.g. `58,5%`. */
    percent: string;
    quantity: number;
    fineQuantity: number;
    valueEur: number;
    valueRon: number;
}

export interface IncomingSummarySection {
    metal: IncomingSummaryMetal;
    rows: IncomingSummaryRow[];
    quantity: number;
    fineQuantity: number;
    valueEur: number;
    valueRon: number;
    /** Weighted average purchase price, EUR per gram. */
    pmp: number;
    /** Weighted average purchase price, RON per gram. */
    pmpRon: number;
}

const SECTION_ORDER: IncomingSummaryMetal[] = ['gold', 'silver', 'other'];

const round2 = (value: number): number => Math.round(value * 100) / 100;

export function classifyMetal(metalType: string): IncomingSummaryMetal {
    const upper = metalType.trim().toUpperCase();
    if (upper.startsWith('AUR')) return 'gold';
    if (upper.startsWith('ARGINT')) return 'silver';
    return 'other';
}

/** Same divisors as the backend report: gold to AUR999.9, everything else to 1000. */
function fineDivisor(metal: IncomingSummaryMetal): number {
    return metal === 'gold' ? 999.99 : 1000;
}

/** Romanian decimal comma, no digit grouping, at most `maxDigits` decimals: 999.9 → `999,9`. */
function formatDecimal(value: number, maxDigits: number): string {
    return value.toLocaleString('ro-RO', { maximumFractionDigits: maxDigits, useGrouping: false });
}

/** A per-mille title as a percentage: 585 → `58,5%`, 750 → `75%`, 999.9 → `99,99%`. */
export function formatPurityPercent(purity: number): string {
    return `${formatDecimal(purity / 10, 3)}%`;
}

function toRow(group: IncomingSummaryGroup, metal: IncomingSummaryMetal): IncomingSummaryRow {
    const purity = Number(group.purity ?? 0);
    const quantity = Number(group.totalQty ?? 0);
    const metalName = (group.metal_type ?? '').trim();
    return {
        key: `${metalName}-${purity}`,
        label: `${metalName} ${formatDecimal(purity, 2)}`.trim(),
        percent: formatPurityPercent(purity),
        quantity: round2(quantity),
        fineQuantity: round2((quantity * purity) / fineDivisor(metal)),
        valueEur: Number(group.totalEuro ?? 0),
        valueRon: Number(group.totalLei ?? 0),
    };
}

function sumOf(rows: IncomingSummaryRow[], pick: (row: IncomingSummaryRow) => number): number {
    return round2(rows.reduce((total, row) => total + pick(row), 0));
}

function toSection(
    metal: IncomingSummaryMetal,
    rows: IncomingSummaryRow[],
): IncomingSummarySection {
    const quantity = sumOf(rows, row => row.quantity);
    const valueEur = sumOf(rows, row => row.valueEur);
    const valueRon = sumOf(rows, row => row.valueRon);
    return {
        metal,
        rows,
        quantity,
        fineQuantity: sumOf(rows, row => row.fineQuantity),
        valueEur,
        valueRon,
        pmp: quantity ? round2(valueEur / quantity) : 0,
        pmpRon: quantity ? round2(valueRon / quantity) : 0,
    };
}

/** Groups the report rows into gold, silver and other sections, each with its totals and PMP. */
export function buildIncomingSummary(groups: IncomingSummaryGroup[]): IncomingSummarySection[] {
    const byMetal = new Map<IncomingSummaryMetal, IncomingSummaryRow[]>();
    for (const group of groups) {
        const metal = classifyMetal(group.metal_type ?? '');
        const rows = byMetal.get(metal) ?? [];
        rows.push(toRow(group, metal));
        byMetal.set(metal, rows);
    }
    return SECTION_ORDER.flatMap(metal => {
        const rows = byMetal.get(metal);
        return rows ? [toSection(metal, rows)] : [];
    });
}
